import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Field, MultiSelect, Select } from '@evogard/ui';
import type { ContaazulAccount, ContaazulAccountAccess } from '@evogard/contract';
import { ACCOUNT_ACCESS_REASON_LABELS, type ExceptionStatus } from '@evogard/domain';
import { api, ok, errorMessage } from '../../lib/api.js';

/**
 * Quem pode USAR uma conta do Conta Azul — o conteúdo do diálogo "Autorização".
 *
 * Dois caminhos, no desenho das permissões da casa:
 *
 * 1. **Grupos autorizados** — quem é do grupo pode usar a conta.
 * 2. **Exceção por pessoa** — `Permitir` ou `Negar`, e ela vence o grupo nos
 *    dois sentidos: a pessoa de um grupo autorizado pode ser proibida, e a de
 *    fora pode ser liberada. "Segue o grupo" apaga a exceção.
 *
 * A coluna do resultado vem da API (`accountAccess`, de `@evogard/domain`), e
 * não de uma conta feita aqui: a tela mostra a regra que o servidor aplica.
 *
 * **Só o vínculo**: nenhuma feature consulta isto ainda, e o texto diz isso.
 */

type Choice = 'segue' | ExceptionStatus;

export function ContaazulAccessPanel({ account }: { account: ContaazulAccount }) {
  const client = useQueryClient();
  const key = ['plataforma', 'integracoes', 'contaazul', 'acesso', account.id];
  const [error, setError] = useState<string | null>(null);
  const [draftGroups, setDraftGroups] = useState<string[] | null>(null);
  const [search, setSearch] = useState('');

  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const r = await api.contaazul.getContaazulAccountAccess({
        params: { id: account.id },
      });
      if (r.status !== 200) {
        throw new Error(errorMessage(r.body, 'Não foi possível carregar a autorização.'));
      }
      return r.body;
    },
  });

  const store = (body: ContaazulAccountAccess) => client.setQueryData(key, body);

  const saveGroups = useMutation({
    mutationFn: async (groupIds: number[]) => {
      const r = await api.contaazul.setContaazulAccountGroups({
        params: { id: account.id },
        body: { groupIds },
      });
      return ok(r, 200, 'Não foi possível salvar os grupos.');
    },
    onSuccess: (body) => {
      store(body);
      setDraftGroups(null);
      setError(null);
    },
    onError: (e: Error) => setError(e.message),
  });

  const saveException = useMutation({
    mutationFn: async ({ userId, choice }: { userId: string; choice: Choice }) => {
      const r = await api.contaazul.setContaazulAccountUserPermission({
        params: { id: account.id },
        body: { userId, status: choice === 'segue' ? null : choice },
      });
      return ok(r, 200, 'Não foi possível salvar a exceção.');
    },
    onSuccess: (body) => {
      store(body);
      setError(null);
    },
    onError: (e: Error) => setError(e.message),
  });

  const data = query.data;
  const grantedNow = useMemo(
    () => (data?.groups ?? []).filter((g) => g.granted).map((g) => String(g.id)),
    [data],
  );
  const selected = draftGroups ?? grantedNow;
  const changed =
    draftGroups !== null &&
    (draftGroups.length !== grantedNow.length ||
      draftGroups.some((id) => !grantedNow.includes(id)));

  const term = search.trim().toLowerCase();
  const members = (data?.members ?? []).filter(
    (m) =>
      !term ||
      m.name.toLowerCase().includes(term) ||
      m.email.toLowerCase().includes(term),
  );
  const allowedCount = (data?.members ?? []).filter((m) => m.allowed).length;

  if (query.isError) {
    return <Alert tone="erro">{query.error.message}</Alert>;
  }
  if (!data) {
    return <p className="text-sm text-texto-suave">Carregando…</p>;
  }

  return (
    <div className="flex flex-col gap-6">

      {error && <Alert tone="erro">{error}</Alert>}

      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-texto">Grupos autorizados</h3>
        <MultiSelect
          /* dentro do diálogo: sem isto a lista abre atrás do véu */
          layer="modal"
          label="Grupos autorizados"
          hiddenLabel
          placeholder="Nenhum grupo autorizado"
          searchPlaceholder="Procurar grupo"
          emptyMessage="Nenhum grupo nesta base."
          options={data.groups.map((g) => ({
            value: String(g.id),
            label: g.archived ? `${g.name} (arquivado)` : g.name,
          }))}
          selected={selected}
          onChange={setDraftGroups}
          disabled={saveGroups.isPending}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            disabled={!changed || saveGroups.isPending}
            onClick={() => saveGroups.mutate(selected.map(Number))}
          >
            {saveGroups.isPending ? 'Salvando…' : 'Salvar grupos'}
          </Button>
          {changed && (
            <Button
              type="button"
              size="sm"
              variant="discreto"
              onClick={() => setDraftGroups(null)}
            >
              Desfazer
            </Button>
          )}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold text-texto">Pessoas</h3>
          <span className="text-xs text-texto-suave">
            {allowedCount} de {data.members.length} podem usar a conta
          </span>
        </div>
        <Field
          label="Procurar pessoa"
          placeholder="Nome ou e-mail"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {members.length === 0 ? (
          <p className="text-sm text-texto-suave">
            {term ? 'Ninguém com esse nome ou e-mail nesta base.' : 'Ninguém nesta base.'}
          </p>
        ) : (
          <ul className="flex max-h-[24rem] flex-col divide-y divide-borda-suave overflow-y-auto rounded-md border border-borda">
            {members.map((m) => (
              <li
                key={m.userId}
                className="grid items-center gap-3 px-3 py-2.5 sm:grid-cols-[1fr_11rem_10rem]"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-texto">{m.name}</p>
                  <p className="truncate text-xs text-texto-suave">
                    {m.email} · {m.groupName ?? 'sem grupo'}
                  </p>
                </div>
                <Select
                  label={`Exceção de ${m.name}`}
                  hiddenLabel
                  value={m.exception ?? 'segue'}
                  disabled={saveException.isPending}
                  onChange={(e) =>
                    saveException.mutate({
                      userId: m.userId,
                      choice: e.target.value as Choice,
                    })
                  }
                >
                  <option value="segue">Segue o grupo</option>
                  <option value="permitir">Permitir</option>
                  <option value="negar">Negar</option>
                </Select>
                <span>
                  <Badge tone={m.allowed ? 'sucesso' : 'neutro'}>
                    {m.allowed ? 'Pode usar' : 'Não pode'}
                  </Badge>
                  <span className="mt-0.5 block text-xs text-texto-suave">
                    {ACCOUNT_ACCESS_REASON_LABELS[m.reason]}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
